/**
 * When a touch is a tap and when it is a drag.
 *
 * Pulled out of `usePanZoom` as a pure reducer after a bug that a screenshot
 * could not have caught and a type could not either: the frame called
 * `setPointerCapture` on **pointerdown**, which is the obvious place for it and
 * the wrong one. Capturing retargets the compatibility mouse events to the
 * capturing element, so `mousedown` and `mouseup` both landed on the frame, so
 * the `click` browsers synthesise from them landed on the frame too — and the
 * `<button>` card under the finger never heard about it. Every node in the tree
 * was unclickable, and panning and zooming worked perfectly, which is the worst
 * possible combination for noticing.
 *
 * The rule, and the reason this file exists rather than a one-line fix:
 *
 *  - **a gesture does not capture the pointer until it has moved.** Under
 *    `TAP_SLOP` it is still a tap, and a tap must reach whatever is under it.
 *  - a second pointer is never a tap, so a pinch captures immediately.
 *  - the pixels below the slop are not applied to the view. A drag therefore
 *    starts from where it *became* a drag, which is also why a click does not
 *    nudge the diagram by a pixel or two.
 */

export interface Point {
  readonly x: number
  readonly y: number
}

export interface Gesture {
  readonly points: ReadonlyMap<number, Point>
  /** Total distance travelled, in pixels, summed over the gesture. */
  readonly travel: number
  /** Distance between two fingers, once there are two. */
  readonly spread: number | null
  /** Has it earned the pointer capture — moved far enough, or gone two-finger? */
  readonly dragging: boolean
}

/** Past this much travel the gesture was a drag and not a tap. A finger never
 *  holds perfectly still, and a thumb on a phone holds still least of all. */
export const TAP_SLOP = 6

export const NO_GESTURE: Gesture = {
  points: new Map(),
  travel: 0,
  spread: null,
  dragging: false,
}

/** What the view should do, and whether the caller should now capture. */
export interface GestureStep {
  readonly gesture: Gesture
  readonly pan?: { readonly dx: number; readonly dy: number }
  readonly zoom?: { readonly factor: number; readonly origin: Point }
  /** True on the step where the gesture first earns the capture. */
  readonly capture: boolean
}

export function begin(gesture: Gesture, id: number, point: Point): Gesture {
  const points = new Map(gesture.points).set(id, point)
  // A second finger is a pinch, and a pinch is never a tap.
  const twoFingers = points.size >= 2
  return {
    points,
    travel: points.size === 1 ? 0 : gesture.travel,
    spread: twoFingers ? spreadOf(points) : null,
    dragging: gesture.dragging || twoFingers,
  }
}

export function move(gesture: Gesture, id: number, point: Point): GestureStep {
  const previous = gesture.points.get(id)
  if (previous === undefined) return { gesture, capture: false }

  const points = new Map(gesture.points).set(id, point)

  if (points.size >= 2) {
    const spread = spreadOf(points)
    const was = gesture.spread
    const next: Gesture = { points, travel: gesture.travel, spread, dragging: true }
    if (was === null || was <= 0 || spread <= 0) return { gesture: next, capture: !gesture.dragging }
    return {
      gesture: next,
      zoom: { factor: spread / was, origin: midpointOf(points) },
      capture: !gesture.dragging,
    }
  }

  const dx = point.x - previous.x
  const dy = point.y - previous.y
  const travel = gesture.travel + Math.abs(dx) + Math.abs(dy)
  const dragging = travel > TAP_SLOP
  const next: Gesture = { points, travel, spread: null, dragging }

  // Below the slop nothing moves. Those pixels belong to a tap that has not
  // finished being a tap yet.
  if (!dragging) return { gesture: next, capture: false }
  return { gesture: next, pan: { dx, dy }, capture: !gesture.dragging }
}

export function end(gesture: Gesture, id: number): Gesture {
  const points = new Map(gesture.points)
  points.delete(id)
  return {
    points,
    travel: gesture.travel,
    spread: points.size >= 2 ? spreadOf(points) : null,
    // Still dragging while any finger is down and it had already become one.
    dragging: points.size > 0 && gesture.dragging,
  }
}

/**
 * Was the gesture that just ended a tap?
 *
 * Asked by a card before it opens its modal. Belt and braces alongside the
 * capture rule: with lazy capture a tap's click reaches the card anyway, and
 * this is what stops a drag that happens to finish over a card from opening it
 * on a device where the click lands regardless.
 */
export function wasTap(gesture: Gesture): boolean {
  return gesture.travel <= TAP_SLOP
}

function spreadOf(points: ReadonlyMap<number, Point>): number {
  const [a, b] = [...points.values()]
  if (a === undefined || b === undefined) return 0
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function midpointOf(points: ReadonlyMap<number, Point>): Point {
  const [a, b] = [...points.values()]
  if (a === undefined || b === undefined) return { x: 0, y: 0 }
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}
